import type { FC, PropsWithChildren } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import { Box, Link, Typography } from '@mui/material'

interface MarkdownProps {
  /** Untrusted markdown, such as model output. Raw HTML is dropped, never rendered. */
  children: string
}

const heading = (variant: 'subtitle1' | 'body1', mt: number): FC<PropsWithChildren> => {
  const Heading: FC<PropsWithChildren> = ({ children }) => (
    <Typography variant={variant} sx={{ fontWeight: 600, mt }}>
      {children}
    </Typography>
  )
  return Heading
}

const components: Components = {
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 1, pl: 2.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 1, pl: 2.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
      {children}
    </Typography>
  ),
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={theme => ({
        my: 1,
        p: 1.5,
        borderRadius: '8px',
        bgcolor: theme.palette.grey[50],
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '1.2rem',
        overflowX: 'auto',
        '& code': { fontSize: 'inherit' },
      })}
    >
      {children}
    </Box>
  ),
  code: ({ children }) => (
    <Box
      component="code"
      sx={theme => ({ fontFamily: theme.typography.fontFamilyMono, fontSize: '0.92em' })}
    >
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{ my: 1, mx: 0, pl: 1.5, borderLeft: 3, borderColor: 'divider', color: 'text.secondary' }}
    >
      {children}
    </Box>
  ),
  // An image is a request to whatever URL the text names, so it renders as its caption instead.
  img: ({ alt }) => (alt ? <span>{alt}</span> : null),
  h1: heading('subtitle1', 1.5),
  h2: heading('subtitle1', 1.5),
  h3: heading('subtitle1', 1.5),
  h4: heading('body1', 1),
  h5: heading('body1', 1),
  h6: heading('body1', 1),
}

/**
 * Sanitized markdown on the theme's typography, for text the CRM did not write: model output
 * is untrusted input, so raw HTML and unsafe URLs are stripped, images render as their
 * caption, and every link opens in a new tab with `rel="noopener noreferrer"`.
 */
export const Markdown: FC<MarkdownProps> = ({ children }) => (
  <ReactMarkdown rehypePlugins={[rehypeSanitize]} components={components}>
    {children}
  </ReactMarkdown>
)
